"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { type RefObject } from "react"

import type { ROLES } from "@/features/auth/api/types"
import {
  fetchAccountsPage,
  type GetUsersSortDirection,
} from "@/features/admin/shared/api/paged-accounts"
import type { AdminFormPayload } from "@/features/admin/shared/api/types"
import {
  useInfiniteListAdapter,
  type InfiniteListScrollOptions,
} from "@/react-query/adapters/use-infinite-query-adapter"
import { apiClient } from "@/react-query/api-client"
import { API_ROUTES } from "@/react-query/api-routes"
import { CACHE_TIME } from "@/react-query/cache-times"
import { queryKeys } from "@/react-query/query-keys"

const adminUsersQueryKey = ["admin", "users"] as const

function invalidateInternalAccountLists(
  queryClient: ReturnType<typeof useQueryClient>
) {
  void queryClient.invalidateQueries({ queryKey: adminUsersQueryKey })
  void queryClient.invalidateQueries({
    queryKey: queryKeys.adminInternalAssignees(),
  })
}

type InfiniteInternalParams = {
  accountTypes: string
  query?: string
  sortBy?: string
  sortDirection?: GetUsersSortDirection
  size?: number
}

/** `totalElements` from the internal list query. Same cache key, so no second request. */
export function useActiveInternalAccountsTotal(
  accountTypes: ROLES[],
  options?: { skip?: boolean }
) {
  const result = useInfiniteInternalAccountsQuery(
    { accountTypes: accountTypes.join(","), size: 20 },
    { enabled: !options?.skip }
  )
  return result.data?.pages[0]?.totalElements ?? 0
}

export function useInfiniteInternalAccountsQuery(
  params: InfiniteInternalParams,
  {
    enabled = true,
    scroll,
  }: { enabled?: boolean; scroll?: InfiniteListScrollOptions } = {}
) {
  return useInfiniteListAdapter({
    queryKey: queryKeys.infiniteAdminUsers({
      types: params.accountTypes,
      query: params.query,
      sortBy: params.sortBy,
      sortDirection: params.sortDirection,
      size: params.size,
    }),
    params,
    staleTime: CACHE_TIME.LIVE,
    gcTime: CACHE_TIME.BALANCED,
    fetchPage: (filters, page) =>
      fetchAccountsPage(
        API_ROUTES.adminInternalAccounts,
        {
          accountTypes: filters.accountTypes,
          query: filters.query,
          sortBy: filters.sortBy,
          sortDirection: filters.sortDirection,
          size: filters.size,
        },
        page
      ),
    getNextPage: (lastPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    enabled,
    selectItems: (page) => page.content,
    scroll,
  })
}

export function useInfiniteInternalAccounts({
  accountTypes,
  query,
  sortBy,
  sortDirection,
  skip = false,
  scrollContainerRef,
}: {
  accountTypes: ROLES[]
  query?: string
  sortBy?: string
  sortDirection?: GetUsersSortDirection
  skip?: boolean
  scrollContainerRef?: RefObject<HTMLElement | null>
}) {
  const result = useInfiniteInternalAccountsQuery(
    {
      accountTypes: accountTypes.join(","),
      query,
      sortBy,
      sortDirection,
      size: 20,
    },
    {
      enabled: !skip,
      scroll: scrollContainerRef
        ? { containerRef: scrollContainerRef }
        : undefined,
    }
  )

  return {
    users: result.items,
    totalElements: result.data?.pages[0]?.totalElements ?? 0,
    isLoading: !skip && result.isLoading,
    isFetching: result.isFetching,
    hasMore: result.hasMore,
    loadMoreRef: result.loadMoreRef,
    loadMore: result.loadMore,
    reset: () => result.refetch(),
  }
}

export function useCreateAdminMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (admin: AdminFormPayload) =>
      apiClient<AdminFormPayload>(API_ROUTES.adminInternalAccounts, {
        method: "POST",
        body: admin,
      }),
    onSuccess: () => invalidateInternalAccountLists(queryClient),
  })
}

export function useUpdateAdminMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...admin }: AdminFormPayload & { id: string }) =>
      apiClient<AdminFormPayload>(API_ROUTES.adminInternalAccount(id), {
        method: "PUT",
        body: admin,
      }),
    onSuccess: () => invalidateInternalAccountLists(queryClient),
  })
}

export function useAssignRelationshipManagerMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      managerId,
      teamLeadId,
    }: {
      managerId: string
      teamLeadId: string
    }) =>
      apiClient<void>(API_ROUTES.adminInternalAccountManager(managerId), {
        method: "PUT",
        body: { teamLeadId },
      }),
    onSuccess: () => invalidateInternalAccountLists(queryClient),
  })
}

export function useUpdateInternalAccountStatusMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string
      status: "activate" | "deactivate"
    }) =>
      apiClient<void>(API_ROUTES.adminInternalAccountStatus(id, status), {
        method: "PUT",
      }),
    onSuccess: () => invalidateInternalAccountLists(queryClient),
  })
}

export function useRetryInternalStreamStatsSyncMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiClient<void>(API_ROUTES.adminInternalRetryStreamStatsSync(id), {
        method: "POST",
      }),
    onSuccess: () => invalidateInternalAccountLists(queryClient),
  })
}
